// tiers.js - single source of truth for Giraops pricing tiers.

export const TIER_PILOT = {
  id: 'pilot',
  name: 'Pilot',
  tagline: 'Early-access - full access, no caps',
  price: '$0',
  priceSub: 'during pilot',
  monthlyPrice: 0,
  isInternal: true,
  caps: {
    ticketsPerMonth: Infinity,
    responders: Infinity,
    skillCategories: Infinity,
    assets: Infinity,
  },
  features: {
    pushDispatch: true,
    whatsappHandoff: true,
    availabilityCalendar: 'full',
    smsEscalation: true,
    voiceFallback: true,
    coverageGapAlerts: true,
    sickDayCoverRequests: true,
    multiSiteRouting: true,
    customBranding: true,
    apiAccess: true,
    ticketCollaboration: true,
    quietHoursForwarding: true,
    uptimeSla: '99.9%',
  },
  support: 'Dedicated',
};

export const TIER_FREE = {
  id: 'free',
  name: 'Free',
  tagline: 'Try it out - small operations',
  price: '$0',
  priceSub: 'forever',
  monthlyPrice: 0,
  isInternal: false,
  ctaLabel: 'Get started - free',
  caps: {
    // Free is capped by SEATS (responders), not by ticket volume — a monthly
    // ticket limit on a 24/7 hotline reads as "we'll stop protecting you".
    ticketsPerMonth: Infinity,
    responders: 3,
    skillCategories: 3,
    assets: 5,
  },
  features: {
    pushDispatch: true,
    whatsappHandoff: true,
    availabilityCalendar: 'none',
    smsEscalation: false,
    voiceFallback: false,
    coverageGapAlerts: false,
    sickDayCoverRequests: false,
    multiSiteRouting: false,
    customBranding: false,
    apiAccess: false,
    ticketCollaboration: false,
    quietHoursForwarding: false,
    uptimeSla: false,
  },
  support: 'Community',
};

export const TIER_BASIC = {
  id: 'basic',
  name: 'Basic',
  tagline: 'Real businesses with regular load',
  price: '$16',
  priceSub: 'per seat / month',
  monthlyPrice: 16,
  isInternal: false,
  accent: true,
  ctaLabel: 'Choose Basic',
  caps: {
    // Unlimited tickets — tiers differ by seats + features, never by ticket volume.
    ticketsPerMonth: Infinity,
    responders: 12,
    skillCategories: 6,
    assets: Infinity,
  },
  features: {
    pushDispatch: true,
    whatsappHandoff: true,
    availabilityCalendar: 'basic',
    smsEscalation: true,
    voiceFallback: true,
    coverageGapAlerts: false,
    sickDayCoverRequests: false,
    multiSiteRouting: false,
    customBranding: false,
    apiAccess: false,
    ticketCollaboration: false,
    quietHoursForwarding: false,
    uptimeSla: false,
  },
  support: 'Email',
};

export const TIER_ENTERPRISE = {
  id: 'enterprise',
  name: 'Enterprise',
  tagline: 'Serious operations, multi-site, branded',
  price: '$29',
  priceSub: 'per seat / month',
  monthlyPrice: 29,
  isInternal: false,
  ctaLabel: 'Choose Enterprise',
  caps: {
    ticketsPerMonth: Infinity,
    responders: Infinity,
    skillCategories: Infinity,
    assets: Infinity,
  },
  features: {
    pushDispatch: true,
    whatsappHandoff: true,
    availabilityCalendar: 'full',
    smsEscalation: true,
    voiceFallback: true,
    coverageGapAlerts: true,
    sickDayCoverRequests: true,
    multiSiteRouting: true,
    customBranding: true,
    apiAccess: true,
    ticketCollaboration: true,
    quietHoursForwarding: true,
    uptimeSla: '99.9%',
  },
  support: 'Dedicated channel',
};

export const PUBLIC_TIERS = [TIER_FREE, TIER_BASIC, TIER_ENTERPRISE];
export const ALL_TIERS = [TIER_PILOT, TIER_FREE, TIER_BASIC, TIER_ENTERPRISE];

// ---- Annual billing (2026-05) ----
// Annual plans are billed for 10 months = "2 months free" (~17% off). The AI
// add-on is billed monthly only (no annual discount) — see Pricing.jsx.
export const ANNUAL_MONTHS_CHARGED = 10;
export const ANNUAL_DISCOUNT_LABEL = '2 months free';

// Yearly total for a tier (monthlyPrice * 10). Returns 0 for free tiers.
export function annualPriceFor(tier) {
  return (tier.monthlyPrice || 0) * ANNUAL_MONTHS_CHARGED;
}

// Effective per-month cost when billed annually (yearly total / 12).
export function effectiveMonthlyAnnual(tier) {
  return annualPriceFor(tier) / 12;
}

// Money formatter — drops the decimals on whole dollars, keeps 2 dp otherwise.
// e.g. 160 -> "$160", 13.333 -> "$13.33".
export function formatMoney(n) {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}

export function resolveTier(planTierString) {
  const found = ALL_TIERS.find((t) => t.id === planTierString);
  return found || TIER_PILOT;
}

export function tierToDisplayRows(tier) {
  return [
    { label: 'Tickets / month', value: formatCap(tier.caps.ticketsPerMonth) },
    { label: 'Responders', value: tier.caps.responders === Infinity ? 'Unlimited' : `up to ${tier.caps.responders}` },
    { label: '24/7 push dispatch', value: tier.features.pushDispatch },
    // 2026-07-09: label updated — WhatsApp handoff replaced by the built-in job
    // chat (spec-ticket-chat.md). Feature KEY kept for data compatibility.
    { label: 'Built-in job chat', value: tier.features.whatsappHandoff },
    { label: 'Skill categories', value: tier.caps.skillCategories === Infinity ? 'Unlimited' : `up to ${tier.caps.skillCategories}` },
    { label: 'QR-asset stickers', value: tier.caps.assets === Infinity ? 'Unlimited' : `up to ${tier.caps.assets}` },
    { label: 'Availability calendar', value: availabilityLabel(tier.features.availabilityCalendar) },
    { label: 'SMS escalation', value: tier.features.smsEscalation },
    { label: 'Voice-call fallback', value: tier.features.voiceFallback },
    { label: 'Coverage gap alerts', value: tier.features.coverageGapAlerts },
    { label: 'Sick-day cover requests', value: tier.features.sickDayCoverRequests },
    { label: 'Ticket transfer + multi-responder collaboration', value: tier.features.ticketCollaboration },
    { label: 'Quiet hours + reception forwarding (phone/email)', value: tier.features.quietHoursForwarding },
    { label: 'Multi-site routing', value: tier.features.multiSiteRouting },
    { label: 'Custom branding (white-label)', value: tier.features.customBranding },
    { label: 'API access', value: tier.features.apiAccess },
    { label: 'Uptime SLA', value: tier.features.uptimeSla },
    { label: 'Support', value: tier.support },
  ];
}

function formatCap(n) {
  if (n === Infinity) return 'Unlimited';
  return String(n);
}

function availabilityLabel(level) {
  if (level === 'none') return false;
  if (level === 'basic') return 'Basic';
  if (level === 'full') return 'Full';
  return false;
}
